import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHead } from "@/pages/admin/AdminLayout";

export default function Categories() {
  const qc = useQueryClient();
  const { data: cats = [] } = useQuery({ queryKey: ["admin-cats"], queryFn: () => api.get("/admin/categories").then((r) => r.data) });
  const [edit, setEdit] = useState(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-cats"] }); qc.invalidateQueries({ queryKey: ["cats"] }); };

  const toggle = async (c) => { await api.patch(`/admin/categories/${c.id}/toggle`); toast.success(`${c.name} ${c.enabled ? "disabled" : "enabled"}`); refresh(); };
  const del = async (c) => { if (!window.confirm(`Delete ${c.name}?`)) return; try { await api.delete(`/admin/categories/${c.id}`); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const save = async (e) => {
    e.preventDefault();
    const body = { name: edit.name, slug: edit.slug || "", tagline: edit.tagline || "", icon: edit.icon || "crosshair", enabled: edit.enabled ?? true, sort: parseInt(edit.sort, 10) || 0 };
    try {
      if (edit.id) await api.put(`/admin/categories/${edit.id}`, body); else await api.post("/admin/categories", body);
      setEdit(null); refresh(); toast.success("Saved");
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="admin-categories">
      <PageHead title="Categories" sub="// enable / disable whole sections of the store">
        <button className="btn-bomb !py-2 !px-4 !text-sm" onClick={() => setEdit({ enabled: true, sort: cats.length + 1 })} data-testid="category-new-button"><Plus size={16} weight="bold" /> New category</button>
      </PageHead>
      <p className="text-sm text-[#999] mb-4 max-w-2xl">Turn a category on and its listings appear in the storefront instantly. Use this to launch Accounts, Boss Carries, Services or anything else you create.</p>
      <div className="border border-[#2a2a2a] overflow-auto">
        <table className="tac-table w-full">
          <thead><tr><th>Live</th><th>Name</th><th>Slug</th><th>Tagline</th><th>Listings</th><th>Order</th><th></th></tr></thead>
          <tbody>
            {cats.map((c) => (
              <tr key={c.id} data-testid={`category-row-${c.slug}`}>
                <td><Switch checked={c.enabled} onCheckedChange={() => toggle(c)} data-testid={`category-toggle-${c.slug}`} /></td>
                <td className="font-bold">{c.name}</td><td className="text-[#888]">{c.slug}</td><td className="text-[#aaa]">{c.tagline}</td><td>{c.count}</td><td>{c.sort}</td>
                <td className="whitespace-nowrap">
                  <button onClick={() => setEdit(c)} className="mr-3 text-[#aaa] hover:text-white transition-colors" data-testid={`category-edit-${c.slug}`}><PencilSimple size={16} /></button>
                  <button onClick={() => del(c)} className="text-[#aaa] hover:text-[#ff2a55] transition-colors" data-testid={`category-delete-${c.slug}`}><Trash size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && (
        <Dialog open onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent className="bg-[#0a0a0a] border border-[#ff2a55]/50 text-white rounded-none" data-testid="category-editor">
            <DialogHeader><DialogTitle className="font-display text-3xl">{edit.id ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <input required className="field" placeholder="Name" value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="category-name-input" />
              {!edit.id && <input className="field" placeholder="Slug (auto)" value={edit.slug || ""} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} />}
              <input className="field" placeholder="Tagline" value={edit.tagline || ""} onChange={(e) => setEdit({ ...edit, tagline: e.target.value })} data-testid="category-tagline-input" />
              <input type="number" className="field" placeholder="Sort order" value={edit.sort ?? 0} onChange={(e) => setEdit({ ...edit, sort: e.target.value })} />
              <label className="flex items-center gap-2 text-xs uppercase tracking-wider"><Switch checked={edit.enabled ?? true} onCheckedChange={(v) => setEdit({ ...edit, enabled: v })} /> Enabled</label>
              <button className="btn-bomb w-full !py-2" data-testid="category-save-button">Save</button>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
